import React, { useId, useRef, useState } from "react";
import styled from "styled-components";
import { FaUpload } from "react-icons/fa";
import { HelpText, SpinnerIcon } from "../Styles";

const UploadWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const SpinnerOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.7);
  z-index: 10;
`;

const FileUploadContainer = styled.div`
  border: 1px dashed ${({ theme }) => theme.button.background};
  border-radius: 10px;
  padding: 10px;
  text-align: center;
  cursor: pointer;
  transition: background-color 0.3s;
  background-color: ${({ theme }) => theme.colors.background};

  &.drag-over {
    border-color: ${({ theme }) => theme.branding.bgColorHover};
  }

  &:hover {
    background-color: ${({ theme }) => theme.branding.bgColorHover};
  }
`;

const FileInput = styled.input`
  display: none;
`;

const UploadIcon = styled(FaUpload)`
  margin-right: 5px;
`;

const UploadButton = styled(HelpText)`
  display: inline-flex;
  align-items: center;
  border: 1px dashed ${({ theme }) => theme.button.background};
  color: ${({ theme }) => theme.colors.text};
  border-radius: 20px;
  padding: 8px 20px;
  font-size: 14px;
`;

const UploadText = styled.div`
  display: block;
  line-height: 1.5;
`;

function FileUploader({
  isUploading = false,
  type,
  fid,
  helpText = "Sube una o varias fotos o escaneo de PDFs aquí",
  handleUploadFile,
}) {
  const fileUploadId = useId();
  const fileUploadDescriptionId = useId();

  const fileInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleFileChange = (e) => {
    e.preventDefault();

    const selectedFiles = e.target.files;
    handleUploadFile(selectedFiles, type, fid);
    e.target.value = null;
  };

  const handleDrop = (e) => {
    e.preventDefault();

    const selectedFiles = e.dataTransfer.files;
    handleUploadFile(selectedFiles, type, fid);
    e.target.value = null;

    setIsDragOver(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleClick = () => {
    fileInputRef.current.click();
  };

  return (
    <UploadWrapper>
      {isUploading && (
        <SpinnerOverlay>
          <SpinnerIcon />
        </SpinnerOverlay>
      )}

      <FileUploadContainer
        className={isDragOver ? "drag-over" : ""}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleClick}
      >
        <FileInput
          aria-describedby={fileUploadDescriptionId}
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf"
          onChange={handleFileChange}
          id={fileUploadId}
          multiple
        />
        <UploadButton>
          <UploadIcon />
          <span>Upload file</span>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor={fileUploadId} hidden>
            Upload file
          </label>
        </UploadButton>
        <UploadText>
          <HelpText id={fileUploadDescriptionId}>
            Drag & Drop or Click to Upload PDFs
          </HelpText>
        </UploadText>
      </FileUploadContainer>
      <HelpText>{helpText}</HelpText>
    </UploadWrapper>
  );
}

export default React.memo(FileUploader);
